import React from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import {
  FaArrowRight, FaArrowLeft, FaCheck, FaLightbulb, FaRocket, FaCode,
} from 'react-icons/fa';
import PageTransition from '../components/PageTransition';
import { services } from './Services';
import {
  fadeInUp, fadeInLeft, fadeInRight, scaleIn,
  staggerContainer, staggerContainerSlow, viewport,
} from '../utils/animations';

const ServiceDetail = () => {
  const { slug } = useParams();
  const svc = services.find((s) => s.slug === slug);

  /* ── Not Found ──────────────────────────────────────────── */
  if (!svc) {
    return (
      <PageTransition>
        <div className="bg-transparent min-h-screen flex items-center justify-center">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="text-center max-w-md mx-auto px-6"
          >
            <motion.div variants={fadeInUp} className="text-6xl mb-6">🔍</motion.div>
            <motion.h1 variants={fadeInUp} className="text-3xl font-black text-white mb-4">
              Service Not Found
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-gray-400 mb-8">
              We couldn't find that service. Explore all our offerings below.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-950 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300"
              >
                <FaArrowLeft className="text-sm" /> View All Services
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </PageTransition>
    );
  }

  const Icon = svc.icon;

  return (
    <PageTransition>
      <div className="bg-transparent min-h-screen">

        {/* ── Hero ────────────────────────────────────────────── */}
        <section className="relative pt-36 pb-24 overflow-hidden">
          <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
          <div className="spotlight w-[600px] h-[600px] top-1/2 left-1/2" />

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            {/* Back link */}
            <motion.div
              variants={fadeInLeft}
              initial="hidden"
              animate="visible"
              className="mb-10"
            >
              <Link
                to="/services"
                className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-white transition-colors duration-300 group"
              >
                <FaArrowLeft className="text-xs group-hover:-translate-x-1 transition-transform duration-300" />
                All Services
              </Link>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              className="max-w-4xl"
            >
              {/* Badge */}
              <motion.div variants={fadeInUp} className="inline-flex mb-6">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${svc.gradient} border border-gray-700 flex items-center justify-center`}>
                  <Icon className="text-3xl text-gray-300" />
                </div>
              </motion.div>

              <motion.h1
                variants={fadeInUp}
                className="text-5xl md:text-6xl lg:text-7xl font-black mb-4 leading-tight"
              >
                <span className="text-white">{svc.title}</span>
              </motion.h1>

              <motion.p
                variants={fadeInUp}
                className="text-gray-400 text-xl md:text-2xl font-medium mb-6 animated-gradient-text"
              >
                {svc.tagline}
              </motion.p>

              <motion.p
                variants={fadeInUp}
                className="text-gray-400 text-base md:text-lg leading-relaxed max-w-3xl mb-10"
              >
                {svc.description}
              </motion.p>

              <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="group flex items-center gap-2 px-8 py-3.5 bg-white text-gray-950 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300 hover:-translate-y-0.5 shine-hover"
                >
                  Start a Project
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
                <Link
                  to="/projects"
                  className="group flex items-center gap-2 px-8 py-3.5 border border-gray-700 text-gray-300 font-semibold rounded-xl hover:border-gray-500 hover:text-white transition-all duration-300 hover:-translate-y-0.5"
                >
                  View Case Studies
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ── Key Features strip ──────────────────────────────── */}
        <section className="py-6 border-y border-gray-800/60">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              variants={staggerContainerSlow}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="flex flex-wrap justify-center gap-4 md:gap-8"
            >
              {svc.features.map((f, i) => (
                <motion.div
                  key={i}
                  variants={fadeInUp}
                  className="flex items-center gap-2 text-sm text-gray-400"
                >
                  <FaCheck className="text-gray-600 flex-shrink-0" />
                  <span>{f}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── Benefits ────────────────────────────────────────── */}
        <section className="relative py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="mb-14 text-center"
            >
              <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-black text-white mb-4">
                Why Choose <span className="animated-gradient-text">{svc.title}</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-gray-400 max-w-lg mx-auto">
                Tangible business impact, not just technical deliverables.
              </motion.p>
            </motion.div>

            <motion.div
              variants={staggerContainerSlow}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              {svc.benefits.map((benefit, i) => (
                <motion.div
                  key={i}
                  variants={i % 2 === 0 ? fadeInLeft : fadeInRight}
                  className="group glass-card rounded-2xl p-8 card-hover-lift"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center group-hover:border-gray-500 transition-colors duration-300">
                      <FaCheck className="text-gray-400 text-sm group-hover:text-white transition-colors duration-300" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg mb-2">{benefit.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{benefit.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── Our Approach / Process ──────────────────────────── */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 dot-pattern pointer-events-none" />

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="mb-14 text-center"
            >
              <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-black text-white mb-4">
                Our <span className="animated-gradient-text">Approach</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-gray-400 max-w-lg mx-auto">
                A proven process that delivers results from kickoff to go-live.
              </motion.p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
              {/* Connecting line */}
              <div className="hidden lg:block absolute top-12 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent" />

              {svc.process.map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={viewport}
                  transition={{ delay: i * 0.15, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                  className="relative text-center group"
                >
                  <div className="relative inline-flex w-20 h-20 rounded-full bg-gray-900/70 border border-gray-700 items-center justify-center mb-6 mx-auto group-hover:border-gray-500 transition-colors duration-300 animate-pulse-glow">
                    <FaCode className="text-2xl text-gray-400 group-hover:text-white transition-colors duration-300" />
                    <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-gradient-to-br from-gray-600 to-gray-800 border border-gray-600 text-xs font-black text-white flex items-center justify-center">
                      {step.num}
                    </span>
                  </div>
                  <h3 className="text-white font-bold text-lg mb-3">{step.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Technologies ────────────────────────────────────── */}
        <section className="relative py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="mb-12 text-center"
            >
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-black text-white mb-3">
                Technologies We <span className="animated-gradient-text">Use</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-gray-400 max-w-lg mx-auto text-sm">
                Best-in-class tools chosen for reliability, performance, and ecosystem maturity.
              </motion.p>
            </motion.div>

            <motion.div
              variants={staggerContainerSlow}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="flex flex-wrap justify-center gap-3"
            >
              {svc.technologies.map((tech, i) => (
                <motion.span
                  key={i}
                  variants={scaleIn}
                  className="px-5 py-2.5 glass-card rounded-full text-sm font-medium text-gray-300 border border-gray-700 hover:border-gray-500 hover:text-white transition-all duration-300 cursor-default"
                >
                  {tech}
                </motion.span>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── Outcomes ────────────────────────────────────────── */}
        <section className="relative py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="glass-card rounded-3xl p-10 md:p-14"
            >
              <motion.div variants={fadeInUp} className="flex justify-center mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gray-800 border border-gray-700 flex items-center justify-center">
                  <FaRocket className="text-gray-300 text-2xl" />
                </div>
              </motion.div>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-black text-white mb-8 text-center">
                What You'll <span className="animated-gradient-text">Achieve</span>
              </motion.h2>
              <motion.div
                variants={staggerContainerSlow}
                className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto"
              >
                {svc.outcomes.map((outcome, i) => (
                  <motion.div
                    key={i}
                    variants={fadeInUp}
                    className="flex items-center gap-3"
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-700 border border-gray-600 flex items-center justify-center">
                      <FaCheck className="text-gray-300 text-xs" />
                    </div>
                    <span className="text-gray-300 text-sm font-medium">{outcome}</span>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ── Other Services ──────────────────────────────────── */}
        <section className="relative py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="mb-10 text-center"
            >
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-black text-white mb-3">
                Other <span className="animated-gradient-text">Services</span>
              </motion.h2>
            </motion.div>

            <motion.div
              variants={staggerContainerSlow}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {services
                .filter((s) => s.slug !== svc.slug)
                .slice(0, 3)
                .map((s, i) => {
                  const OtherIcon = s.icon;
                  return (
                    <motion.div
                      key={i}
                      variants={fadeInUp}
                      whileHover={{ y: -6, transition: { duration: 0.2 } }}
                      className="group glass-card rounded-2xl p-7 card-hover-lift"
                    >
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${s.gradient} border border-gray-700 flex items-center justify-center mb-5 group-hover:border-gray-500 transition-colors duration-300`}>
                        <OtherIcon className="text-xl text-gray-300 group-hover:text-white transition-colors duration-300" />
                      </div>
                      <h3 className="text-white font-bold text-lg mb-2">{s.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed mb-5">{s.short}</p>
                      <Link
                        to={`/services/${s.slug}`}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-400 group-hover:text-white transition-colors duration-300 link-underline"
                      >
                        Learn More <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform duration-300" />
                      </Link>
                    </motion.div>
                  );
                })}
            </motion.div>
          </div>
        </section>

        {/* ── CTA ─────────────────────────────────────────────── */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/0 via-gray-800/25 to-gray-900/0 pointer-events-none" />
          <div className="spotlight w-96 h-96 top-1/2 left-1/2" />

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="glass-card rounded-3xl p-12 md:p-16 text-center max-w-3xl mx-auto"
            >
              <motion.div variants={scaleIn} className="flex justify-center mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gray-800 border border-gray-700 flex items-center justify-center">
                  <FaLightbulb className="text-gray-300 text-2xl" />
                </div>
              </motion.div>
              <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-black text-white mb-4">
                Ready to get started?
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-gray-400 text-lg mb-10">
                Let's schedule a free 30-minute discovery call and outline what's possible.
              </motion.p>
              <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/contact"
                  className="group flex items-center gap-2 px-10 py-4 bg-white text-gray-950 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300 hover:-translate-y-1 shine-hover"
                >
                  Book a Free Call
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
                <Link
                  to="/services"
                  className="group flex items-center gap-2 px-8 py-4 border border-gray-700 text-gray-300 font-semibold rounded-xl hover:border-gray-500 hover:text-white transition-all duration-300 hover:-translate-y-1"
                >
                  <FaArrowLeft className="text-xs" /> All Services
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

      </div>
    </PageTransition>
  );
};

export default ServiceDetail;

